import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import moment from "moment";
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  TextField,
  Paper,
  CircularProgress,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { useApi } from "../../utils/api";
import {
  workoutsToContextSummary,
  exerciseNamesFromWorkouts,
} from "../../utils/programCreatorContext";

function newMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function welcomeMessage(clientName) {
  return clientName
    ? `I can draft a sample week for ${clientName} based on their recent training. Describe the split, focus, or changes you want.`
    : "Select a client, then describe the week you want to program.";
}

function buildPromptFromConversation(priorMessages, latestUserText) {
  if (!priorMessages.length) return latestUserText;

  const history = priorMessages
    .map((m) => {
      if (m.role === "user") return `Coach: ${m.content}`;
      if (m.outline) return `Assistant (program outline):\n${m.outline}`;
      return `Assistant: ${m.content}`;
    })
    .join("\n\n");

  return `Previous conversation:\n${history}\n\nNew request: ${latestUserText}`;
}

const outlinePreSx = {
  m: 0,
  p: 1.25,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  fontFamily: "Roboto Mono, monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

function OutlinePreviewDialog({
  open,
  onClose,
  outline,
  weekLabel,
  onApply,
  fullScreen,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      scroll="paper"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
        }}
      >
        <span>Sample program{weekLabel ? ` — ${weekLabel}` : ""}</span>
        <IconButton onClick={onClose} size="small" aria-label="Close preview">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          component="pre"
          sx={{
            ...outlinePreSx,
            border: 0,
            borderRadius: 0,
            p: 2.5,
            fontSize: fullScreen ? "0.9rem" : "0.82rem",
            lineHeight: 1.5,
            minHeight: fullScreen ? "auto" : 360,
          }}
        >
          {outline}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => {
            onApply(outline);
            onClose();
          }}
        >
          Apply to new program
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ChatBubble({ message, onApply, weekLabel, fullScreenPreview }) {
  const isUser = message.role === "user";
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.5,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: "92%",
          p: 1.5,
          bgcolor: isUser ? "primary.main" : "grey.100",
          color: isUser ? "primary.contrastText" : "text.primary",
          borderRadius: 2,
          border: message.error ? 1 : 0,
          borderColor: message.error ? "error.main" : "transparent",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.5,
            fontSize: "0.875rem",
          }}
        >
          {message.content}
        </Typography>
        {message.outline && (
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.75,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                }}
              >
                Sample program{weekLabel ? ` — ${weekLabel}` : ""}
              </Typography>
              <Button
                size="small"
                startIcon={<OpenInFullIcon />}
                onClick={() => setPreviewOpen(true)}
                sx={{
                  minWidth: 0,
                  py: 0.25,
                  px: 0.75,
                  fontSize: "0.7rem",
                  color: "text.secondary",
                }}
              >
                Expand
              </Button>
            </Box>
            <Box
              component="pre"
              onClick={() => setPreviewOpen(true)}
              sx={{
                ...outlinePreSx,
                fontSize: "0.72rem",
                lineHeight: 1.45,
                maxHeight: 200,
                overflow: "auto",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.light",
                  bgcolor: "action.hover",
                },
              }}
            >
              {message.outline}
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
              <LoadingButton
                variant="contained"
                size="small"
                onClick={() => onApply(message.outline)}
              >
                Apply to new program
              </LoadingButton>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPreviewOpen(true)}
              >
                View full program
              </Button>
            </Box>
            <OutlinePreviewDialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              outline={message.outline}
              weekLabel={weekLabel}
              onApply={onApply}
              fullScreen={fullScreenPreview}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}

const ProgramCreatorAiDrawer = ({
  open,
  onClose,
  clientId,
  clientName,
  weekLabel,
  onApplyProgram,
}) => {
  const { generateProgramOutline, getWorkoutsForUser } = useApi();
  const getWorkoutsRef = useRef(getWorkoutsForUser);
  getWorkoutsRef.current = getWorkoutsForUser;
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextSummary, setContextSummary] = useState("");
  const [usedExerciseNames, setUsedExerciseNames] = useState([]);
  const chatEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(scrollToBottom, 50);
    return () => window.clearTimeout(t);
  }, [messages, generating, open, scrollToBottom]);

  const loadContext = useCallback(async () => {
    if (!clientId) {
      setContextSummary("No workouts in the last month.");
      setUsedExerciseNames([]);
      setContextLoading(false);
      return;
    }
    setContextLoading(true);
    try {
      const from = moment().subtract(1, "month").startOf("day").unix();
      const to = moment().endOf("day").unix();
      const data = await getWorkoutsRef.current(clientId, from, to);
      const workouts = Array.isArray(data) ? data : [];
      setContextSummary(workoutsToContextSummary(workouts));
      setUsedExerciseNames(exerciseNamesFromWorkouts(workouts));
    } catch (e) {
      console.error(e);
      setContextSummary("No workouts in the last month.");
      setUsedExerciseNames([]);
    } finally {
      setContextLoading(false);
    }
  }, [clientId]);

  useLayoutEffect(() => {
    if (!open) {
      setContextLoading(false);
      return;
    }

    setContextLoading(true);
    setInput("");
    setMessages([
      {
        id: newMessageId(),
        role: "assistant",
        content: welcomeMessage(clientName),
      },
    ]);
  }, [open, clientId, clientName]);

  useEffect(() => {
    if (!open) return;
    loadContext();
  }, [open, clientId, loadContext]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || generating || !clientId || contextLoading) return;

    const userMsg = { id: newMessageId(), role: "user", content: trimmed };
    const prior = messages.filter((m) => m.role === "user" || m.outline);
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setGenerating(true);

    try {
      const prompt = buildPromptFromConversation(prior, trimmed);
      const result = await generateProgramOutline({
        prompt,
        contextSummary,
        usedExerciseNames,
        weekLabel,
      });
      const outline = result?.outline?.trim();
      if (!outline) {
        throw new Error("No program outline returned. Try again.");
      }

      setMessages((prev) => [
        ...prev,
        {
          id: newMessageId(),
          role: "assistant",
          content: "Here's a sample program based on your request:",
          outline,
        },
      ]);
    } catch (e) {
      console.error(e);
      const errText =
        e?.message ||
        "Could not generate a program outline. Try again or rephrase your request.";
      setMessages((prev) => [
        ...prev,
        {
          id: newMessageId(),
          role: "assistant",
          content: errText,
          error: true,
        },
      ]);
    } finally {
      setGenerating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100vw" : 420,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <AutoAwesomeIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          AI program assistant
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      {contextLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 2,
            flexShrink: 0,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {!contextLoading && !generating && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, pt: 1.5, flexShrink: 0, display: "block" }}
        >
          Uses the last month of this client&apos;s workouts as context. Generated
          programs appear in the chat — review, then apply to the new program.
        </Typography>
      )}

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          py: 2,
        }}
      >
        {messages.map((message) => (
          <ChatBubble
            key={message.id}
            message={message}
            onApply={onApplyProgram}
            weekLabel={weekLabel}
            fullScreenPreview={isMobile}
          />
        ))}
        {generating && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5 }}>
            <Paper
              elevation={0}
              sx={{ p: 1.5, bgcolor: "grey.100", borderRadius: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Drafting program…
              </Typography>
            </Paper>
          </Box>
        )}
        <div ref={chatEndRef} />
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          size="small"
          placeholder={
            clientId
              ? "e.g. 4-day upper/lower, progress main lifts, swap accessories…"
              : "Select a client first"
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!clientId || generating || contextLoading}
        />
        <LoadingButton
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 1.5 }}
          endIcon={<SendIcon />}
          loading={generating}
          disabled={!clientId || !input.trim() || contextLoading}
          onClick={handleSend}
        >
          Send
        </LoadingButton>
      </Box>
    </Drawer>
  );
};

export default ProgramCreatorAiDrawer;
